/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
import { Injectable } from '@angular/core';
import { PermissionService } from '@dm/core';
import { LicenseKey } from 'app/common/constants/license-key';
import { AuthService, ToolService } from 'app/common/services';
import { FeatureConfig } from 'app/config/feature';
import { ProductConfig, ShardSwitch, SWITCH } from 'app/config/switch';
import { ServiceAdStrService } from 'app/feature/settings/users-security/domain-auth/service-ad/service-ad-str.service';
import { ServiceLdapStrService } from 'app/feature/settings/users-security/domain-auth/service-ldap/service-ldap-str.service';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type DataConfig, type Tip, DataTypeE, DataURL, GlobalSearchStrService } from '../../global-search-str.service';
import { GlobalSearchConfigService } from '../global-search-config.service';

@Injectable({ providedIn: 'root' })
export class ConfigSettingsStrService {
    settingsDataMap: { [key: string]: DataConfig };
    constructor(
        private configService: GlobalSearchConfigService,
        private str: GlobalSearchStrService,
        private ldapStrService: ServiceLdapStrService,
        private adStrService: ServiceAdStrService,
        private permissionService: PermissionService,
        private toolService: ToolService,
        private authService: AuthService,
    ) { }

    /**
     * 返回dataMap
     */
    getDataMap(): { [key: string]: DataConfig } {
        this.settingsDataMap = {
            /**
             * 设备信息
             */
            [DataTypeE.deviceInfo]: {
                type: DataTypeE.deviceInfo,
                url: '/settings/basic-infomation/device-info',
                operation: [
                    {
                        searchKey: 'common.batchConfig_label',
                        action: 'searchCreate',
                        actionParams: 'batchConfiguration',
                        permission: ['@batch_configuration_R'],
                    },
                    {
                        searchKey: 'system.exportData_task_title_label',
                        action: 'searchCreate',
                        actionParams: 'exportData',
                        hide: this.authService.isRemoteAssistanceUser(),
                        permission: [['alarm_W', 'running_data_W', 'performance_W']],
                    },
                    {
                        searchKey: 'common.device_restart_label',
                        action: 'searchCreate',
                        actionParams: 'restart',
                        permission: ['boot_W'],
                        noRestartShutDownFeature: !FeatureConfig.IsShowDeviceRestartAndShutDownButton,
                    },
                    {
                        searchKey: 'common.device_shutdown_label',
                        action: 'searchCreate',
                        actionParams: 'shutdown',
                        permission: ['boot_W'],
                        noRestartShutDownFeature: !FeatureConfig.IsShowDeviceRestartAndShutDownButton,
                    },
                    {
                        searchKey: 'common.upgrade_label',
                        action: 'searchCreate',
                        actionParams: 'upgrade',
                        permission: ['support_major_W'],
                        noRestartShutDownFeature: !FeatureConfig.IsSupportOTAUpgrade,
                        hide: ProductConfig.isHideUpgrade,
                    },
                ],
                page: {
                    searchKey: 'common.device_info_label',
                    defaultSearch: true,
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.monitorTitle,
                },
            },
            /**
             * 设备时间
             */
            [DataTypeE.systemTime]: {
                type: DataTypeE.systemTime,
                url: '/settings/basic-infomation/device-time',
                page: {
                    searchKey: 'common.systemTime_title',
                },
            },
            /**
             * dns服务
             */
            [DataTypeE.dnsService]: {
                type: DataTypeE.dnsService,
                url: '/settings/basic-infomation/dns-service',
                page: {
                    searchKey: 'common.dnsService_title',
                    permission: [['dns_server_R', 'dns_zone_R']],
                },
            },
            /**
             * 升级
             */
            [DataTypeE.upgrade]: {
                type: DataTypeE.upgrade,
                url: '/settings/basic-infomation/upgrade',
                tab: this.str.i18n.upgradeTab,
                page: {
                    searchKey: 'common.upgrade_label',
                    permission: ['support_major_W'],
                    noOem: true,
                    noProdRelease: true,
                },
                operation: [
                    {
                        searchKey: 'common.upgrade_label',
                        action: 'searchCreate',
                        permission: ['support_major_W'],
                        noRestartShutDownFeature: FeatureConfig.IsSupportOTAUpgrade,
                    },
                ],
                hide: ProductConfig.isHideUpgrade,
            },
            /**
             * 邮件服务
             */
            [DataTypeE.emailService]: {
                type: DataTypeE.emailService,
                url: '/settings/basic-infomation/email-service',
                page: {
                    searchKey: 'settings.EventNotification_mailService_title',
                    permission: ['alarm_R'],
                },
            },

            /**
             * 块服务
             */
            [DataTypeE.blockService]: {
                type: DataTypeE.blockService,
                url: '/settings/block-storage-service',
                license: [LicenseKey.SmartThin],
                page: {
                    searchKey: 'common.blockService_title',
                    permission: [['target_R', 'initiator_R', 'isns_R', 'host_R']],
                },
                hide: SWITCH.IS_DIRECT_MODE,
            },

            /**
             * NFS服务
             */
            [DataTypeE.nfsService]: {
                type: DataTypeE.nfsService,
                url: '/settings/file-storage-service/nfs-service',
                isNASFeature: true,
                page: {
                    searchKey: 'settings.fileStorageService_term_nfsService_tittle',
                    permission: ['nfs_service_R'],
                },
            },
            /**
             * CIFS服务
             */
            [DataTypeE.cifsService]: {
                type: DataTypeE.cifsService,
                url: '/settings/file-storage-service/cifs-service',
                isNASFeature: true,
                page: {
                    searchKey: 'settings.fileStorageService_term_cifsService_title',
                    permission: ['cifs_service_R'],
                },
            },
            /**
             * NDMP服务
             */
            ...(!ProductConfig.isSupportNDMP) ? {} : {
                [DataTypeE.NDMPService]: {
                    type: DataTypeE.NDMPService,
                    url: '/settings/file-storage-service/file-ndmp-service',
                    isNASFeature: true,
                    page: {
                        searchKey: 'settings.ndmp_task_title',
                        permission: ['@ndmp_service_R'],
                    },
                },
            },
            /**
             *  审计日志管理
             */
            [DataTypeE.auditLog]: {
                type: DataTypeE.auditLog,
                url: '/settings/file-storage-service/audit-log',
                tab: this.str.i18n.auditLogManagerTab,
                isNASFeature: true,
                page: {
                    searchKey: 'settings.auditLog_task_title_management',
                    permission: ['@audit_strategy_R'],
                },
            },
            /**
             *  审计日志导出到syslog
             */
            [DataTypeE.AUDIT_LOG_EXPORT]: {
                type: DataTypeE.AUDIT_LOG_EXPORT,
                url: '/settings/file-storage-service/audit-log',
                tab: this.str.i18n.auditLogExportTab,
                isNASFeature: true,
                page: {
                    searchKey: 'settings.auditLog_task_title_export_to_auditLog',
                    permission: ['@audit_strategy_R'],
                },
            },
            /**
             * 防病毒服务
             */
            [DataTypeE.antivirus]: {
                type: DataTypeE.antivirus,
                url: '/settings/file-storage-service/antivirus',
                isNASFeature: true,
                page: {
                    searchKey: 'settings.Antivirus_task_title',
                    permission: ['antivirus_R'],
                },
                hide: !ProductConfig.isSupportAntivirus,
            },
            /**
             * 证书管理
             */
            [DataTypeE.certificateManagement]: {
                type: DataTypeE.certificateManagement,
                url: '/settings/certificate-management',
                tab: this.str.i18n.certificateManagement,
                page: {
                    searchKey: 'common.certificateManagement_title',
                    permission: ['certificate_R'],
                },
            },
            /**
             * 证书自动签发
             */
            [DataTypeE.certificateAuto]: {
                type: DataTypeE.certificateAuto,
                url: '/settings/certificate-management',
                tab: this.str.i18n.certificateAuto,
                page: {
                    searchKey: 'settings.systemSetting_task_caAutoApply_title',
                    permission: ['certificate_R'],
                },
            },
            /**
             * 内置CA
             */
            [DataTypeE.builtInCa]: {
                type: DataTypeE.builtInCa,
                url: '/settings/certificate-management',
                tab: this.str.i18n.builtInCaTitle,
                hide: !ShardSwitch.BUILT_IN_CA,
                page: {
                    searchKey: '内置CA',
                    permission: ['certificate_R'],
                },
            },

            /**
             * 用户
             */
            [DataTypeE.user]: {
                type: DataTypeE.user,
                url: '/settings/users-security/user-role',
                tab: this.str.i18n.userTab,
                operation: [
                    {
                        searchKey: 'settings.userAndRole_task_createUser_title',
                        action: 'add',
                    },
                ],
                page: {
                    searchKey: 'settings.userAndRole_task_Users_title',
                },
            },
            /**
             * 角色
             */
            [DataTypeE.role]: {
                type: DataTypeE.role,
                url: '/settings/users-security/user-role',
                tab: this.str.i18n.roleTab,
                operation: [
                    {
                        searchKey: 'settings.userAndRole_task_roleManagement_createRole1_title',
                        action: 'create',
                    },
                ],
                page: {
                    searchKey: 'settings.userAndRole_task_Roles_title',
                },
            },
            /**
             * 安全策略
             */
            [DataTypeE.securityPolicy]: {
                type: DataTypeE.securityPolicy,
                url: '/settings/users-security/security-policy',
                page: {
                    searchKey: 'common.securityPolicy_title_wintitle',
                },
            },
            /**
             * 管理平面LDAP域
             */
            [DataTypeE.manageLdap]: {
                type: DataTypeE.manageLdap,
                url: '/settings/users-security/domain-auth',
                tab: this.str.i18n.manageLdapTab,
                page: {
                    searchKey: 'settings.domainAuth_term_manageLDAP_tittle',
                    permission: ['ldap_R'],
                },
            },
            /**
             * 文件服务LDAP域
             * 因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.ldapDomain]: {
                type: DataTypeE.ldapDomain,
                url: '/settings/users-security/domain-auth',
                tab: this.str.i18n.ldapDomainTab,
                isNASFeature: true,
                page: {
                    searchKey: 'settings.domainAuth_term_serviceLDAP_tittle',
                    permission: ['domain_R'],
                },
                getCombinationObservable: (vstoreId: string): Observable<Tip[]> =>
                    this.configService.getData(vstoreId, DataURL.ldapdomain, { vstoreId }, undefined, true).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: data.primaryIp ? this.str.i18n.enabled : this.str.i18n.disabled,
                                },
                                {
                                    label: this.str.i18n.protocolLab,
                                    value: this.ldapStrService.protocalMap[data.TRANSFERTYPE],
                                },
                                {
                                    label: this.str.i18n.portIdLab,
                                    value: data.PORTNUM,
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             * 文件服务NIS域
             * 因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.nisDomain]: {
                type: DataTypeE.nisDomain,
                url: '/settings/users-security/domain-auth',
                tab: this.str.i18n.nisDomainTab,
                isNASFeature: true,
                page: {
                    searchKey: 'settings.domainAuth_term_serviceNIS_tittle',
                    permission: ['domain_R'],
                },
                getCombinationObservable: (vstoreId: string): Observable<Tip[]> =>
                    this.configService.getData(vstoreId, DataURL.nisdomain, { vstoreId }, undefined, true).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: data.NISDOMAIN ? this.str.i18n.enabled : this.str.i18n.disabled,
                                },
                                {
                                    label: this.str.i18n.domainNameLab,
                                    value: data.NISDOMAIN,
                                },
                            ];
                            return result;
                        }),
                    ),
            },
            /**
             * 文件服务AD域
             * 因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.adDomain]: {
                type: DataTypeE.adDomain,
                url: '/settings/users-security/domain-auth',
                tab: this.str.i18n.adDomainTab,
                isNASFeature: true,
                page: {
                    searchKey: 'settings.domainAuth_term_serviceAD_title',
                    permission: ['domain_R'],
                },
                getCombinationObservable: (vstoreId: string): Observable<Tip[]> =>
                    this.configService.getData(vstoreId, DataURL.addomain, { vstoreId }).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.adStrService.domainStatusType[data.DOMAINSTATUS],
                                },
                                {
                                    label: this.str.i18n.domainNameLab,
                                    value: data.FULLDOMAINNAME,
                                },
                            ];
                            return result;
                        }),
                    ),
                hide: ProductConfig.isHideAdDomain,
            },
            /**
             * 多因子认证
             */
            [DataTypeE.multifactorAuthen]: {
                type: DataTypeE.multifactorAuthen,
                url: '/settings/users-security/user-config',
                page: {
                    searchKey: 'settings.userAndSecurity_term_multifactorAuthen_label',
                    permission: ['user_R'],
                },
            },
            /**
             * SSO服务器
             */
            ...(this.permissionService.isSuperAdmin() ?
                {
                    [DataTypeE.ssoServer]: {
                        type: DataTypeE.ssoServer,
                        url: '/settings/users-security/sso-server',
                        page: {
                            searchKey: 'settings.userAndSecurity_term_SSO_title',
                        },
                    },
                } :
                {}
            ),

            /**
             * License管理
             */
            ...(SWITCH.IS_DIRECT_MODE ?
                {} :
                {
                    [DataTypeE.manageLicense]: {
                        type: DataTypeE.manageLicense,
                        url: '/settings/license-management',
                        page: {
                            searchKey: 'common.manageLicense_title',
                        },
                    },
                }
            ),

            /**
             * 密钥服务
             */
            [DataTypeE.keyService]: {
                type: DataTypeE.keyService,
                url: '/settings/key-service',
                page: {
                    searchKey: 'common.keyService_title',
                    permission: ['kmc_R'],
                },
            },

            /**
             * 告警通知
             */
            [DataTypeE.alarmNotification]: {
                type: DataTypeE.alarmNotification,
                url: '/settings/alarm/alarm-notify',
                page: {
                    searchKey: 'common.alarmNotification_title',
                    permission: ['alarm_R'],
                },
            },
            /**
             * 告警转储
             */
            [DataTypeE.dumpAlarm]: {
                type: DataTypeE.dumpAlarm,
                url: '/settings/alarm/alarm-dump',
                page: {
                    searchKey: 'common.dumpAlarm_title',
                    permission: ['alarm_R'],
                },
            },
            /**
             * 告警屏蔽
             */
            [DataTypeE.maskAlarm]: {
                type: DataTypeE.maskAlarm,
                url: '/settings/alarm/alarm-mask',
                page: {
                    searchKey: 'common.maskAlarm_title',
                    permission: ['alarm_R'],
                },
            },
            /**
             * 事件通知
             */
            [DataTypeE.eventNotification]: {
                type: DataTypeE.eventNotification,
                url: '/settings/alarm/alarm-event',
                page: {
                    searchKey: 'settings.EventNotification_title',
                    permission: ['alarm_R'],
                },
            },
            /**
             * 告警级别
             */
            [DataTypeE.alarmSeverity]: {
                type: DataTypeE.eventNotification,
                url: '/settings/alarm/alarm-level',
                page: {
                    searchKey: 'settings.alarmNotification_email_alarmLevel_label',
                    permission: ['alarm_R'],
                },
            },

            /**
             * SNMP协议
             */
            [DataTypeE.snmpProtocol]: {
                type: DataTypeE.snmpProtocol,
                url: '/settings/snmp/snmp-protocol',
                page: {
                    searchKey: 'common.snmpProtocol_title',
                    permission: ['snmp_R'],
                },
            },
            /**
             * SNMP安全策略
             */
            [DataTypeE.snmpSecurityPolicy]: {
                type: DataTypeE.snmpSecurityPolicy,
                url: '/settings/snmp/snmp-security-policy',
                page: {
                    searchKey: 'common.snmpSecurityPolicy_title',
                    permission: ['snmp_R'],
                },
            },

            /**
             * 监控设置
             */
            [DataTypeE.monitorSetting]: {
                type: DataTypeE.monitorSetting,
                url: '/settings/monitor',
                page: {
                    searchKey: 'common.monitorSetting_title',
                    permission: ['performance_R'],
                },
            },

            /**
             * eService设置
             */
            [DataTypeE.eService]: {
                type: DataTypeE.eService,
                url: '/settings/e-service',
                page: {
                    searchKey: 'settings.eService_title',
                    permission: ['call_home_R'],
                    noOem: true,
                    noRoceFeature: !FeatureConfig.IsSupportEService,
                },
            },

            /**
             * 容器设置
             */
            [DataTypeE.Container]: {
                type: DataTypeE.Container,
                url: '/settings/container-configuration',
                isContainerFeature: true,
                page: {
                    searchKey: 'settings.container_task_setting_title',
                    permission: FeatureConfig.IsSupportVM ? ['vm_service_R'] : ['container_service_R'],
                    license: [
                        [
                            LicenseKey.SmartContainerLicense,
                            LicenseKey.SmartContainerLicenseStandard,
                            LicenseKey.SmartContainerLicensePremium,
                            LicenseKey.CloudBackup,
                            LicenseKey.DataBackUpCapacity,
                            LicenseKey.AntiRansomware,
                            LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                            LicenseKey.S3,
                            LicenseKey.VirtualEnvironmentEnabler,
                        ],
                    ],
                },
            },
        };

        return this.settingsDataMap;
    }
}
